import { listOf } from '../entity/entity';
import type { Arc, Status } from '../plan/arc';
import { isInFlight } from '../plan/arc';

/**
 * Which arc holds which capture — the reverse of `sources:`.
 *
 * One pass over the arcs the board already caches answers it for the whole
 * corpus, so both views and every row's right-hand lane come from data that
 * is held anyway.
 *
 * A citation naming an anchor (`path ^topic`) takes ONE topic, not the file.
 * Such a capture is still ungathered: a recording that leaves the pile
 * forever because a single topic was taken never gets looked at again.
 */

export interface GatheredIn {
  arcName: string;
  arcPath: string;
  title: string;
  status: Status;
  /** True when this arc cites only anchors inside the file. */
  partial: boolean;
}

export type Gathering = Map<string, GatheredIn[]>;

export function buildGathering(arcs: Arc[]): Gathering {
  const out: Gathering = new Map();

  for (const arc of arcs) {
    const whole = new Set<string>();
    const anchored = new Set<string>();

    for (const source of listOf(arc.frontMatter['sources'])) {
      const ref = source.trim().replace(/^\[\[|\]\]$/g, '');
      const path = ref.split(' ^')[0]!.trim();
      if (!path.endsWith('.md')) continue;
      (ref.includes(' ^') ? anchored : whole).add(path);
    }

    for (const path of new Set([...whole, ...anchored])) {
      const entry: GatheredIn = {
        arcName: arc.name,
        arcPath: arc.path,
        title: arc.title,
        status: arc.status,
        partial: !whole.has(path),
      };
      const list = out.get(path);
      if (list) list.push(entry);
      else out.set(path, [entry]);
    }
  }

  return out;
}

export function isGathered(entries: GatheredIn[] | undefined): boolean {
  return entries?.some((e) => !e.partial) ?? false;
}

/**
 * Feeding work in flight — what the work being carried right now is built on,
 * as opposed to what has been filed.
 *
 * IN_FLIGHT rather than `active` alone: an arc waiting on you is being carried
 * too, and the thing it is waiting on is usually exactly what you would want to
 * look at. This read `active` only, which quietly disagreed with the sentence
 * above it from the day the fifth status arrived.
 */
export function feedsInFlight(entries: GatheredIn[] | undefined): boolean {
  return entries?.some((e) => isInFlight(e.status)) ?? false;
}

/**
 * One status for a capture that several arcs may cite.
 *
 * A card is not the place to list them — the entity page is, and it already
 * does. What a card owes is whether this thing is feeding anything live, so
 * the liveliest citation wins: active over needs-input over next over parked
 * over done.
 */
const LIVELINESS: Status[] = ['active', 'needs-input', 'next', 'parked', 'done'];

export function topStatus(entries: GatheredIn[] | undefined): Status | null {
  if (!entries || entries.length === 0) return null;
  return LIVELINESS.find((status) => entries.some((e) => e.status === status)) ?? null;
}
